import { AnimatePresence, motion } from 'framer-motion'
import { rem } from 'polished'
import React, { useState } from 'react'
import { formatPrice } from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { ProductPrice } from '@public/components/ProductPrice'
import { BUTTON_INTENT, BUTTON_VARIANT, Button } from 'components/Button'
import Icon from 'components/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { type CartContextItem } from 'providers/cart'
import { useUserAuth } from 'providers/userAuth'
import { findDiscount } from 'routes/utils/findDiscount'
import { COLOR_INTENT, FONT_SIZE, FONT_WEIGHT, RADIUS, SPACE } from 'Theme'
import { type CalculatePriceCheckoutQuery } from 'types/graphql-generated'
import { DiscountDisplay } from './DiscountDisplay'
import { SummaryItem } from './SummaryItem'

const TRANSITION_DURATION = 0.3
const TRANSITION_EASE = [0.4, 0.0, 0.2, 1]
const COLLAPSED_SUM_TOTAL_HEIGHT = rem(44)

const PriceInfoText = styled(Text)`
  font-style: italic;
  font-size: ${FONT_SIZE.PX_13};
  color: ${COLOR_INTENT.GRAY_70};
`

interface CollapsibleSummaryProps {
  cartItems: CartContextItem[]
  discountItems?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['discountItems']
  prices: {
    shipping: number | string
    tax: number | string
    totalDiscount?: {
      total: number
      areAllItemsDiscountEligible: boolean
      linkToTerms: string
    } | null
    total: number | string
    retailSubtotal?: number | string
    tradeSubtotal?: number | string
    currencyIsoCode: string
    couponDiscount?: number
  }
  isExpandable?: boolean
  isOnShippingStep?: boolean
  isTaxExemptionApplied?: boolean
}

export const CollapsibleSummary: React.FC<
  React.PWC<CollapsibleSummaryProps>
> = ({
  cartItems,
  discountItems,
  prices,
  isTaxExemptionApplied,
  isExpandable = false,
  isOnShippingStep = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useUserAuth()
  const itemsCount = cartItems.length

  return (
    <Box
      backgroundColor={COLOR_INTENT.SUMMARY.COLLAPSIBLE_SUMMARY.BACKGROUND}
      borderRadius={RADIUS.PX_6}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        p={SPACE.PX_4}
        pl={SPACE.PX_16}
        height={COLLAPSED_SUM_TOTAL_HEIGHT}
        gap={SPACE.PX_8}
      >
        <Text>
          <Text fontWeight={FONT_WEIGHT.MEDIUM}>
            {isOnShippingStep ? 'Sum Total' : 'Subtotal'}
          </Text>
          {` (${itemsCount} item${itemsCount !== 1 ? 's' : ''})`}
        </Text>
        <Grid
          gridTemplateColumns="1fr max-content"
          alignItems="center"
          gridGap={SPACE.PX_8}
        >
          <Text
            fontWeight={FONT_WEIGHT.MEDIUM}
            data-test-id={DATA_TEST_ID.SHOWROOM_CART_SUMMARY_SUBTOTAL_PRICE}
          >
            {typeof prices.total === 'number'
              ? formatPrice({
                  price: prices.total,
                  currency: prices.currencyIsoCode,
                })
              : prices.total}
          </Text>
          {isExpandable && (
            <Button
              icon={isOpen ? Icon.ChevronUpSmall : Icon.ChevronDownSmall}
              variant={BUTTON_VARIANT.MINIMAL}
              intent={BUTTON_INTENT.NONE}
              onClick={() => {
                setIsOpen((prevState) => !prevState)
              }}
              color={COLOR_INTENT.SUMMARY.COLLAPSIBLE_SUMMARY.BUTTON_COLOR}
              aria-label="expand summary"
            />
          )}
        </Grid>
      </Flex>
      {user?.isTradeCapable && !isExpandable && (
        <Box pl={SPACE.PX_16} pr={SPACE.PX_12} pb={SPACE.PX_4}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            py={SPACE.PX_4}
          >
            <Text fontSize={FONT_SIZE.PX_12}>Retail subtotal</Text>
            <Text fontSize={FONT_SIZE.PX_12}>
              {typeof prices.retailSubtotal === 'number'
                ? formatPrice({
                    price: prices.retailSubtotal,
                    currency: prices.currencyIsoCode,
                  })
                : prices.retailSubtotal}
            </Text>
          </Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            py={SPACE.PX_4}
          >
            <Text
              fontWeight={FONT_WEIGHT.MEDIUM}
              color={COLOR_INTENT.TEXT_ON_LIGHT}
              fontSize={FONT_SIZE.PX_12}
            >
              Trade savings
            </Text>
            <Text
              fontSize={FONT_SIZE.PX_12}
              fontWeight={FONT_WEIGHT.MEDIUM}
              color={COLOR_INTENT.TEXT_ON_LIGHT}
            >
              {typeof prices.retailSubtotal === 'number' &&
              typeof prices.tradeSubtotal === 'number'
                ? formatPrice({
                    price: prices.retailSubtotal - prices.tradeSubtotal,
                    currency: prices.currencyIsoCode,
                  })
                : prices.retailSubtotal}
            </Text>
          </Flex>
        </Box>
      )}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial="collapsed"
            animate="open"
            exit="collapsed"
            style={{ overflow: 'hidden' }}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{
              duration: TRANSITION_DURATION,
              ease: TRANSITION_EASE,
            }}
          >
            <Grid py={SPACE.PX_16} px={SPACE.PX_12} gridGap={SPACE.PX_16}>
              {cartItems.map(
                ({
                  cartQuantity,
                  productVariant: {
                    id,
                    images,
                    price,
                    tradePrice,
                    title,
                    optionValues,
                    managedCustomFields,
                  },
                }) => {
                  const discount = findDiscount({ id, discountItems })
                  return (
                    <SummaryItem
                      title={title}
                      productImageUrl={images[0]?.url}
                      productImageAlt={images[0]?.alt}
                      cartQuantity={cartQuantity}
                      price={price}
                      tradePrice={tradePrice}
                      discount={discount}
                      optionValues={optionValues}
                      managedCustomFields={managedCustomFields}
                      key={id}
                    />
                  )
                }
              )}
              {user?.isTradeCapable && (
                <Flex justifyContent="space-between">
                  <Text>Retail subtotal</Text>
                  <Text>
                    {typeof prices.retailSubtotal === 'number'
                      ? formatPrice({
                          price: prices.retailSubtotal,
                          currency: prices.currencyIsoCode,
                        })
                      : prices.retailSubtotal}
                  </Text>
                </Flex>
              )}
              {user?.isTradeCapable && (
                <Flex justifyContent="space-between">
                  <Text
                    fontWeight={FONT_WEIGHT.MEDIUM}
                    color={COLOR_INTENT.TEXT_ON_LIGHT}
                  >
                    Trade savings
                  </Text>
                  <Text
                    fontWeight={FONT_WEIGHT.MEDIUM}
                    color={COLOR_INTENT.TEXT_ON_LIGHT}
                  >
                    {typeof prices.retailSubtotal === 'number' &&
                    typeof prices.tradeSubtotal === 'number'
                      ? formatPrice({
                          price: prices.retailSubtotal - prices.tradeSubtotal,
                          currency: prices.currencyIsoCode,
                        })
                      : prices.retailSubtotal}
                  </Text>
                </Flex>
              )}
              {user?.isTradeCapable && (
                <Flex justifyContent="space-between">
                  <Text>Trade subtotal</Text>
                  <Text>
                    {typeof prices.tradeSubtotal === 'number'
                      ? formatPrice({
                          price: prices.tradeSubtotal,
                          currency: prices.currencyIsoCode,
                        })
                      : prices.tradeSubtotal}
                  </Text>
                </Flex>
              )}
              {prices.totalDiscount && (
                <DiscountDisplay
                  allItemsDiscountEligible={
                    prices.totalDiscount.areAllItemsDiscountEligible
                  }
                  totalDiscount={prices.totalDiscount.total}
                  linkToTerms={prices.totalDiscount.linkToTerms}
                />
              )}
              <Flex justifyContent="space-between">
                <Text>Shipping</Text>
                {typeof prices.shipping === 'number' ? (
                  <ProductPrice
                    price={{
                      total: prices.shipping,
                      currencyIsoCode: prices.currencyIsoCode,
                    }}
                  />
                ) : (
                  <PriceInfoText>{prices.shipping}</PriceInfoText>
                )}
              </Flex>
              <Box>
                <Flex justifyContent="space-between">
                  <Text>Sales Tax</Text>
                  {typeof prices.tax === 'number' ? (
                    <ProductPrice
                      price={{
                        total: prices.tax,
                        currencyIsoCode: prices.currencyIsoCode,
                      }}
                    />
                  ) : (
                    <PriceInfoText>{prices.tax}</PriceInfoText>
                  )}
                </Flex>
                {isTaxExemptionApplied && (
                  <Text fontSize={FONT_SIZE.PX_12} color={COLOR_INTENT.GRAY_70}>
                    Tax exemption applied
                  </Text>
                )}
              </Box>
              {prices?.couponDiscount > 0 && (
                <Flex justifyContent="space-between">
                  <Text
                    fontSize={FONT_SIZE.PX_14}
                    fontWeight={FONT_WEIGHT.MEDIUM}
                    color={COLOR_INTENT.DISCOUNT.CODE_APPLIED}
                  >
                    Gift card
                  </Text>
                  <ProductPrice
                    price={{
                      total: prices.couponDiscount,
                      currencyIsoCode: prices.currencyIsoCode,
                    }}
                    color={COLOR_INTENT.DISCOUNT.CODE_APPLIED}
                    isDiscount
                  />
                </Flex>
              )}
            </Grid>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
